import { Datepicker } from "@/components/datepicker/Datepicker.tsx";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { useWorkflowUpdateTriggerRecurringRule } from "@/queries/workflows/workflow.query.ts";
import {
  ApiWorkflowTriggerRecurringRule,
  CalendarRuleType,
  EndType,
  frequencies,
  calendarRuleTypes,
  endTypes,
  ordinals,
  Ordinal,
  RecurringFrequency,
  weekDays,
  WeekDay,
  WorkflowTriggerRecurringRuleForm,
  WorkflowTriggerRecurringRuleProps,
  WorkflowTriggerRecurringRuleRef,
} from "@/types/workflows/workflow.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Input, Multiselect, Select, Stack, Text, Title } from "@skailer-com/uikit";
import { isValid } from "date-fns";
import { forwardRef, useEffect, useImperativeHandle, useMemo } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Frequency, Options, RRule, Weekday } from "rrule";
import * as yup from "yup";

const weekDayTranslationKeys: Record<WeekDay, string> = {
  [WeekDay.mo]: "common:monday",
  [WeekDay.tu]: "common:tuesday",
  [WeekDay.we]: "common:wednesday",
  [WeekDay.th]: "common:thursday",
  [WeekDay.fr]: "common:friday",
  [WeekDay.sa]: "common:saturday",
  [WeekDay.su]: "common:sunday",
};

const frequencyMap: Record<RecurringFrequency, Frequency> = {
  [RecurringFrequency.daily]: RRule.DAILY,
  [RecurringFrequency.weekly]: RRule.WEEKLY,
  [RecurringFrequency.monthly]: RRule.MONTHLY,
  [RecurringFrequency.yearly]: RRule.YEARLY,
};

const frequencyByValue: Partial<Record<Frequency, RecurringFrequency>> = {
  [RRule.DAILY]: RecurringFrequency.daily,
  [RRule.WEEKLY]: RecurringFrequency.weekly,
  [RRule.MONTHLY]: RecurringFrequency.monthly,
  [RRule.YEARLY]: RecurringFrequency.yearly,
};

const weekDayMap: Record<WeekDay, Weekday> = {
  [WeekDay.mo]: RRule.MO,
  [WeekDay.tu]: RRule.TU,
  [WeekDay.we]: RRule.WE,
  [WeekDay.th]: RRule.TH,
  [WeekDay.fr]: RRule.FR,
  [WeekDay.sa]: RRule.SA,
  [WeekDay.su]: RRule.SU,
};

const getCurrentWeekDay = (): WeekDay => weekDays[(new Date().getDay() + 6) % 7];
const getWeekDay = (weekday: number): WeekDay => weekDays[weekday] ?? WeekDay.mo;
const toUtcDate = (date: Date, hour: number = 0): Date =>
  new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), hour));
const fromUtcDate = (date: Date): Date =>
  new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
const getRuleString = (rule: RRule): string => rule.toString().replace(/^RRULE:/, "");

const parseRule = (
  rule: ApiWorkflowTriggerRecurringRule | null,
  workDayStartHour: number
): WorkflowTriggerRecurringRuleForm => {
  const today = new Date();
  const defaults: WorkflowTriggerRecurringRuleForm = {
    frequency: RecurringFrequency.weekly,
    interval: 1,
    weeklyDays: [getCurrentWeekDay()],
    calendarRuleType: CalendarRuleType.month_day,
    monthDay: today.getDate(),
    ordinal: 1,
    ordinalWeekday: getCurrentWeekDay(),
    yearMonth: today.getMonth() + 1,
    endType: EndType.never,
    until: null,
    count: null,
    dateStart: today,
    hour: workDayStartHour,
  };

  if (!rule) return defaults;

  try {
    const options = RRule.fromString(rule.rrule).origOptions;
    const byWeekday = Array.isArray(options.byweekday)
      ? options.byweekday
      : options.byweekday != null
        ? [options.byweekday]
        : [];
    const weekdays = byWeekday.map((weekday) => {
      if (weekday instanceof Weekday) return weekday;
      if (typeof weekday === "number") return new Weekday(weekday);
      return weekDayMap[weekday];
    });
    const ordinalWeekday = weekdays.find((weekday) => weekday.n != null);
    const byMonthDay = Array.isArray(options.bymonthday)
      ? options.bymonthday[0]
      : options.bymonthday;
    const byMonth = Array.isArray(options.bymonth) ? options.bymonth[0] : options.bymonth;

    return {
      ...defaults,
      frequency: frequencyByValue[options.freq ?? RRule.WEEKLY] ?? RecurringFrequency.weekly,
      interval: options.interval ?? 1,
      weeklyDays: weekdays.map((weekday) => getWeekDay(weekday.weekday)),
      calendarRuleType: ordinalWeekday
        ? CalendarRuleType.ordinal_weekday
        : CalendarRuleType.month_day,
      monthDay: byMonthDay ?? defaults.monthDay,
      ordinal: (ordinalWeekday?.n as Ordinal | undefined) ?? 1,
      ordinalWeekday: ordinalWeekday ? getWeekDay(ordinalWeekday.weekday) : defaults.ordinalWeekday,
      yearMonth: byMonth ?? defaults.yearMonth,
      endType: options.count ? EndType.count : options.until ? EndType.until : EndType.never,
      until: options.until ? fromUtcDate(options.until) : null,
      count: options.count ?? null,
      dateStart: isValid(rule.date_start) ? rule.date_start : today,
      hour: rule.hour,
    };
  } catch {
    return {
      ...defaults,
      dateStart: isValid(rule.date_start) ? rule.date_start : today,
      hour: rule.hour,
    };
  }
};

const buildRule = (values: WorkflowTriggerRecurringRuleForm, includeDateStart = false): RRule => {
  const options: Partial<Options> = {
    freq: frequencyMap[values.frequency],
  };

  if (values.interval > 1) options.interval = values.interval;
  if (includeDateStart) options.dtstart = toUtcDate(values.dateStart, values.hour);

  if (values.frequency === RecurringFrequency.weekly) {
    options.byweekday = values.weeklyDays.map((weekday) => weekDayMap[weekday]);
  }

  if (
    values.frequency === RecurringFrequency.monthly ||
    values.frequency === RecurringFrequency.yearly
  ) {
    if (values.calendarRuleType === CalendarRuleType.month_day) {
      options.bymonthday = values.monthDay;
    } else {
      options.byweekday = weekDayMap[values.ordinalWeekday].nth(values.ordinal);
    }
  }

  if (values.frequency === RecurringFrequency.yearly) options.bymonth = values.yearMonth;
  if (values.endType === EndType.count && values.count) options.count = values.count;
  if (values.endType === EndType.until && values.until) {
    options.until = new Date(
      Date.UTC(
        values.until.getFullYear(),
        values.until.getMonth(),
        values.until.getDate(),
        23,
        59,
        59
      )
    );
  }

  return new RRule(options);
};

export const WorkflowTriggerRecurringRule = forwardRef<
  WorkflowTriggerRecurringRuleRef,
  WorkflowTriggerRecurringRuleProps
>(({ workflow, rule }, ref) => {
  const { t, i18n } = useTranslation();
  const { data: employeeMe } = useEmployeeMe();
  const defaultValues = useMemo(
    () => parseRule(rule, employeeMe?.organization.work_day_start_hour ?? 9),
    [employeeMe?.organization.work_day_start_hour, rule]
  );

  const schema = useMemo<yup.ObjectSchema<WorkflowTriggerRecurringRuleForm>>(
    () =>
      yup.object({
        frequency: yup.mixed<RecurringFrequency>().oneOf(frequencies).required(),
        interval: yup
          .number()
          .typeError(t("errors:field_number_required"))
          .integer(t("errors:field_number_required"))
          .min(1, t("api_errors:minimum_value_must", { minimum: 1 }))
          .required(t("errors:field_required")),
        weeklyDays: yup
          .array(yup.mixed<WeekDay>().oneOf(weekDays).required())
          .defined()
          .test("weekly-days", t("errors:field_required"), (value, context) =>
            context.parent.frequency === RecurringFrequency.weekly ? value.length > 0 : true
          ),
        calendarRuleType: yup.mixed<CalendarRuleType>().oneOf(calendarRuleTypes).required(),
        monthDay: yup
          .number()
          .typeError(t("errors:field_number_required"))
          .integer(t("errors:field_number_required"))
          .min(1, t("api_errors:minimum_value_must", { minimum: 1 }))
          .max(31, t("api_errors:maximum_value_must", { maximum: 31 }))
          .required(t("errors:field_required")),
        ordinal: yup.mixed<Ordinal>().oneOf(ordinals).required(),
        ordinalWeekday: yup.mixed<WeekDay>().oneOf(weekDays).required(),
        yearMonth: yup
          .number()
          .typeError(t("errors:field_number_required"))
          .integer(t("errors:field_number_required"))
          .min(1, t("api_errors:minimum_value_must", { minimum: 1 }))
          .max(12, t("api_errors:maximum_value_must", { maximum: 12 }))
          .required(t("errors:field_required")),
        endType: yup.mixed<EndType>().oneOf(endTypes).required(),
        until: yup
          .date()
          .nullable()
          .defined()
          .test("until-required", t("errors:field_required"), (value, context) =>
            context.parent.endType === EndType.until ? value != null : true
          )
          .test(
            "until-after-start",
            t("errors:end_date_should_be_greater_start_date"),
            (value, context) =>
              context.parent.endType !== EndType.until ||
              value == null ||
              value >= context.parent.dateStart
          ),
        count: yup
          .number()
          .typeError(t("errors:field_number_required"))
          .nullable()
          .defined()
          .integer(t("errors:field_number_required"))
          .min(1, t("api_errors:minimum_value_must", { minimum: 1 }))
          .test("count-required", t("errors:field_required"), (value, context) =>
            context.parent.endType === EndType.count ? value != null : true
          ),
        dateStart: yup.date().required(t("errors:field_required")),
        hour: yup
          .number()
          .typeError(t("errors:field_number_required"))
          .integer(t("errors:field_number_required"))
          .min(0, t("api_errors:minimum_value_must", { minimum: 0 }))
          .max(23, t("api_errors:maximum_value_must", { maximum: 23 }))
          .required(t("errors:field_required")),
      }),
    [t]
  );

  const form = useForm<WorkflowTriggerRecurringRuleForm>({
    mode: "onSubmit",
    resolver: yupResolver(schema),
    defaultValues,
  });

  const {
    control,
    getValues,
    register,
    reset,
    trigger,
    formState: { errors, isDirty },
  } = form;

  const updateRecurringRule = useWorkflowUpdateTriggerRecurringRule(control);
  const values = useWatch({ control });
  const frequency = values.frequency ?? defaultValues.frequency;
  const interval = values.interval ?? defaultValues.interval;
  const calendarRuleType = values.calendarRuleType ?? defaultValues.calendarRuleType;
  const endType = values.endType ?? defaultValues.endType;

  useEffect(() => {
    if (!isDirty) reset(defaultValues);
  }, [defaultValues, isDirty, reset]);

  const saveData = async (): Promise<boolean> => {
    if (!(await trigger())) return false;
    if (!isDirty && rule) return true;

    const currentValues = getValues();
    try {
      const result = await updateRecurringRule.mutateAsync({
        workflowId: workflow.id,
        workflowVersionId: workflow.current_version_id,
        payload: {
          rrule: getRuleString(buildRule(currentValues)),
          date_start: currentValues.dateStart,
          hour: currentValues.hour,
        },
      });
      return result.success;
    } catch {
      return false;
    }
  };

  useImperativeHandle(ref, () => ({ saveData }));

  const frequencyOptions = [
    {
      id: RecurringFrequency.daily,
      title: t("core:reminder_period_day", { count: interval }),
    },
    {
      id: RecurringFrequency.weekly,
      title: t("core:reminder_period_week", { count: interval }),
    },
    {
      id: RecurringFrequency.monthly,
      title: t("core:reminder_period_month", { count: interval }),
    },
    {
      id: RecurringFrequency.yearly,
      title: t("plurals:unit_years", { count: interval }),
    },
  ];
  const intervalOptions = Array.from({ length: 99 }, (_, index) => ({
    id: String(index + 1),
    title: String(index + 1),
  }));
  const weekDayOptions = weekDays.map((value) => ({
    id: value,
    title: t(weekDayTranslationKeys[value]),
  }));
  const calendarRuleTypeOptions = calendarRuleTypes.map((value) => ({
    id: value,
    title: t(`workflow:trigger_recurring_calendar_rule_${value}`),
  }));
  const ordinalOptions = ordinals.map((value) => ({
    id: value,
    title: t(`workflow:trigger_recurring_ordinal_${value === -1 ? "last" : value}`),
  }));
  const monthOptions = Array.from({ length: 12 }, (_, index) => ({
    id: index + 1,
    title: new Intl.DateTimeFormat(i18n.language, { month: "long" }).format(
      new Date(2020, index, 1)
    ),
  }));
  const endTypeOptions = endTypes.map((value) => ({
    id: value,
    title: t(`workflow:trigger_recurring_end_${value}`),
  }));
  const hourOptions = Array.from({ length: 24 }, (_, hour) => ({
    id: String(hour),
    title: `${String(hour).padStart(2, "0")}:00`,
  }));

  return (
    <Stack>
      <Stack gap="md">
        <Stack direction="horizontal" gap="sm" items="center">
          <Text>
            {t("workflow:trigger_recurring_repeat_every", {
              repeat: t("workflow:trigger_recurring_frequency"),
              every: t(
                `plurals:every_${frequency === RecurringFrequency.weekly ? "feminine" : "masculine"}`,
                { count: interval }
              ).toLocaleLowerCase(),
            })}
          </Text>
          <Controller
            control={control}
            name="interval"
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Select
                variant="button"
                emptyTitle={t("common:period")}
                value={String(value)}
                options={intervalOptions}
                onChange={(nextValue) => onChange(Number(nextValue))}
                error={error?.message}
                searchable
                required
              />
            )}
          />
          <Controller
            control={control}
            name="frequency"
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Select
                key={`recurring-frequency-${interval}`}
                variant="button"
                emptyTitle={t("common:period")}
                value={value}
                options={frequencyOptions}
                onChange={onChange}
                error={error?.message}
                required
              />
            )}
          />
        </Stack>

        {frequency !== RecurringFrequency.daily && (
          <Box variant={"border"}>
            {frequency === RecurringFrequency.weekly && (
              <Controller
                control={control}
                name="weeklyDays"
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <Multiselect
                    label={t("workflow:trigger_recurring_weekdays")}
                    emptyTitle={t("common:select")}
                    values={value}
                    options={weekDayOptions}
                    onChange={onChange}
                    isClearable
                    error={error?.message}
                    required
                  />
                )}
              />
            )}
            {(frequency === RecurringFrequency.monthly ||
              frequency === RecurringFrequency.yearly) && (
              <>
                {frequency === RecurringFrequency.yearly && (
                  <Controller
                    control={control}
                    name="yearMonth"
                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                      <Select
                        label={t("workflow:trigger_recurring_month")}
                        emptyTitle={t("common:select")}
                        value={value}
                        options={monthOptions}
                        onChange={(nextValue) => onChange(Number(nextValue))}
                        error={error?.message}
                        required
                      />
                    )}
                  />
                )}
                <Controller
                  control={control}
                  name="calendarRuleType"
                  render={({ field: { value, onChange } }) => (
                    <Select
                      label={t("workflow:trigger_recurring_calendar_rule")}
                      emptyTitle={t("common:select")}
                      value={value}
                      options={calendarRuleTypeOptions}
                      onChange={onChange}
                      required
                    />
                  )}
                />
                {calendarRuleType === CalendarRuleType.month_day ? (
                  <Input
                    type="number"
                    min={1}
                    max={31}
                    label={t("workflow:trigger_recurring_month_day")}
                    error={errors.monthDay?.message}
                    required
                    {...register("monthDay")}
                  />
                ) : (
                  <Stack direction="horizontal" gap={"md"}>
                    <Box className={"flex-1"}>
                      <Controller
                        control={control}
                        name="ordinal"
                        render={({ field: { value, onChange } }) => (
                          <Select
                            label={t("workflow:trigger_recurring_ordinal")}
                            emptyTitle={t("common:select")}
                            value={value}
                            options={ordinalOptions}
                            onChange={(nextValue) => onChange(Number(nextValue))}
                            required
                          />
                        )}
                      />
                    </Box>
                    <Box className={"flex-1"}>
                      <Controller
                        control={control}
                        name="ordinalWeekday"
                        render={({ field: { value, onChange } }) => (
                          <Select
                            label={t("workflow:trigger_recurring_weekday")}
                            emptyTitle={t("common:select")}
                            value={value}
                            options={weekDayOptions}
                            onChange={onChange}
                            required
                          />
                        )}
                      />
                    </Box>
                  </Stack>
                )}
              </>
            )}
          </Box>
        )}
      </Stack>

      <Title
        header={t("workflow:trigger_recurring_section_start")}
        size="xs"
        paddingTop
        paddingBottom
      />
      <Stack direction="horizontal" gap="md">
        <Box className={"flex-1"}>
          <Controller
            control={control}
            name="dateStart"
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Datepicker
                label={t("common:start_date")}
                value={value}
                onChange={onChange}
                error={error?.message}
                required
              />
            )}
          />
        </Box>
        <Box className={"flex-1"}>
          <Controller
            control={control}
            name="hour"
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Select
                label={t("workflow:trigger_schedule_hour")}
                emptyTitle={t("common:select")}
                value={String(value)}
                options={hourOptions}
                onChange={(nextValue) => onChange(Number(nextValue))}
                error={error?.message}
                searchable
                required
              />
            )}
          />
        </Box>
      </Stack>

      <Title header={t("workflow:trigger_recurring_end")} size="xs" paddingTop paddingBottom />
      <Controller
        control={control}
        name="endType"
        render={({ field: { value, onChange } }) => (
          <Select
            label={t("workflow:trigger_recurring_end_type")}
            emptyTitle={t("common:select")}
            value={value}
            options={endTypeOptions}
            onChange={onChange}
            required
          />
        )}
      />
      {endType === EndType.until && (
        <Controller
          control={control}
          name="until"
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Datepicker
              label={t("common:end_date")}
              value={value ?? undefined}
              onChange={onChange}
              error={error?.message}
              required
            />
          )}
        />
      )}
      {endType === EndType.count && (
        <Input
          type="number"
          min={1}
          label={t("workflow:trigger_recurring_end_count_label")}
          error={errors.count?.message}
          required
          {...register("count")}
        />
      )}
    </Stack>
  );
});
